import { type MutableRefObject, useEffect, useRef } from 'react';

/**
 * Тип хранящийся в рефе
 */
type CheckAsync = {
    /** Замаунтен ли хук/компонент его использующий */
    isMount: boolean;
    /** Сохраняет метку запроса и возвращает маркер запроса */
    save: (label?: string) => number;
    /** Проверяет маркер на принадлежность запросу */
    check: (labelOrMarker: string | number, marker?: number) => boolean;
};

export type RefCheckAsync = MutableRefObject<CheckAsync>;

const initial: CheckAsync = { isMount: false, save: () => -1, check: () => false };

function makeId() {
    return Date.now();
}

/**
 * Hook создает ref c id запроса
 * По которому можно проверить асинхронный ответ принадлежит текущему запросу или нет
 * Так же проверяет смонтирован элемент или нет (что способствуют не изменению стейта размонтированного компонента)
 *
 * ```
 * const checkAsync = useCheckAsync();
 *
 * useEffect(()=>{
 *   const marker = checkAsync.current.save('fetch');
 *
 *   fetch(...)
 *     .then(() => {
 *        if(!checkAsync.current.check('fetch', marker)) return;
 *        ...
 *     })
 *     .catch(() => {
 *        if(!checkAsync.current.check('fetch', marker)) return;
 *        ...
 *     })
 * }, []);
 *
 * ```
 *
 * Если в компоненте только один запрос, label можно опустить
 *
 * ```
 * const checkAsync = useCheckAsync();
 *
 * useEffect(()=>{
 *   const marker = checkAsync.current.save();
 *
 *   fetch(...)
 *     .then(() => {
 *        if(!checkAsync.current.check(marker)) return;
 *        ...
 *     })
 *     .catch(() => {
 *        if(!checkAsync.current.check(marker)) return;
 *        ...
 *     })
 * }, []);
 * ```
 * @returns ref c id запроса
 * @kind helper
 */
export function useCheckAsync(): RefCheckAsync {
    const ref = useRef<CheckAsync>(initial);

    useEffect(() => {
        let timestamp: number;
        const map: Record<string, number> = Object.create(null);

        ref.current = {
            isMount: true,
            save: (label) => {
                const marker = makeId();

                if (label) {
                    map[label] = marker;

                    return map[label];
                }

                timestamp = marker;

                return timestamp;
            },
            check: (labelOrMarker: string | number, marker?: number) => {
                if (typeof marker !== 'undefined') {
                    return map[labelOrMarker] === marker;
                }

                return timestamp === labelOrMarker;
            },
        };

        return () => {
            ref.current = initial;
        };
    }, []);

    return ref;
}
